/**
 * Modal — Overlay modal com dark theme e tamanhos pré-definidos
 */
import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { T, CT } from '../../styles/tokens';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const sizeWidths: Record<ModalSize, number> = {
  sm: 320,
  md: 420,
  lg: 560,
  xl: 720,
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: number;
  size?: ModalSize;
  maxHeight?: number | string;
  children: any;
  footer?: any;
}

export function Modal({ open, onClose, title, width, size, maxHeight, children, footer }: ModalProps) {
  const finalWidth = width ?? (size ? sizeWidths[size] : 420);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: CT.modal.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        onClick={(e: any) => e.stopPropagation()}
        style={{
          width: finalWidth, maxWidth: '90vw', maxHeight: maxHeight ?? '85vh',
          background: CT.modal.bg,
          border: `1px solid ${T.borderHover}`,
          borderRadius: CT.modal.radius,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: `${T.space4}px ${T.space5}px`,
          borderBottom: `1px solid ${T.border}`,
        }}>
          <span style={{ fontSize: T.textLg, fontWeight: T.weightBold, color: T.text }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: T.textSecondary, padding: T.space1, display: 'flex',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Body */}
        <div style={{ padding: T.space5, overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
        {/* Footer */}
        {footer && (
          <div style={{
            padding: `${T.space3}px ${T.space5}px`,
            borderTop: `1px solid ${T.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: T.space2,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
